import React, { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface ImportSavingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string | null;
  onSuccess: () => void;
  isLoading?: boolean;
}

const ImportSavingsModal: React.FC<ImportSavingsModalProps> = ({
  isOpen,
  onClose,
  groupId,
  onSuccess,
  isLoading: propLoading = false,
}) => {
  const { user, userProfile } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const downloadTemplate = () => {
    const headers = ['national_id', 'amount', 'date', 'notes'];
    const example = ['NID123456', '1000', new Date().toISOString().split('T')[0], 'Savings for last week'];
    const csvContent = [headers.join(','), example.join(',')].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'savings_import_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Please select a valid CSV file.');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !groupId || !user) {
      setError('Missing file or group information.');
      return;
    }

    setLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim() !== '');
        if (lines.length < 2) {
          setError('The CSV file is empty or missing data rows.');
          setLoading(false);
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const expectedHeaders = ['national_id', 'amount', 'date'];
        
        const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
        if (missingHeaders.length > 0) {
          setError(`Missing required columns: ${missingHeaders.join(', ')}`);
          setLoading(false);
          return;
        }

        const rows = lines.slice(1).map((line, index) => {
          const values = line.split(',').map(v => v.trim());
          const row: any = {};
          headers.forEach((header, i) => {
            row[header] = values[i];
          });
          row.lineNumber = index + 2;
          return row;
        });

        // 1. Get unique National IDs to resolve to user IDs
        const uniqueNIDs = [...new Set(rows.map(r => r.national_id).filter(Boolean))];
        
        // 2. Lookup profiles
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, national_id')
          .in('national_id', uniqueNIDs);

        if (profilesError) throw profilesError;

        const nidToIdMap = (profiles || []).reduce((acc, p) => {
          if (p.national_id) acc[p.national_id] = p.id;
          return acc;
        }, {} as Record<string, string>);

        // 3. Process records
        let successCount = 0;
        let failCount = 0;
        const rowErrors: string[] = [];

        // Determine status
        const isClerk = userProfile?.group_roles?.includes('clerk');
        const status = isClerk ? 'pending_approval' : 'approved';

        const entriesToInsert = [];

        for (const row of rows) {
          const memberId = nidToIdMap[row.national_id];
          const amount = parseFloat(row.amount);
          const date = row.date ? new Date(row.date) : new Date();

          if (!memberId) {
            rowErrors.push(`Row ${row.lineNumber}: Member with National ID "${row.national_id}" not found.`);
            failCount++;
            continue;
          }

          if (isNaN(amount) || amount <= 0) {
            rowErrors.push(`Row ${row.lineNumber}: Invalid amount "${row.amount}".`);
            failCount++;
            continue;
          }

          if (isNaN(date.getTime())) {
            rowErrors.push(`Row ${row.lineNumber}: Invalid date "${row.date}".`);
            failCount++;
            continue;
          }

          entriesToInsert.push({
            group_id: groupId,
            member_id: memberId,
            amount: amount,
            contribution_date: date.toISOString(),
            notes: row.notes?.trim() || null,
            status: status,
            recorded_by: user.id,
          });
        }

        if (entriesToInsert.length > 0) {
          const { error: insertError } = await supabase
            .from('contributions')
            .insert(entriesToInsert);

          if (insertError) throw insertError;
          successCount = entriesToInsert.length;
        }

        if (successCount > 0) {
          alert(`Successfully imported ${successCount} savings record(s).${failCount > 0 ? ` ${failCount} failed.` : ''}`);
          onSuccess();
          onClose();
        } else if (failCount > 0) {
          setError(`Import failed:\n${rowErrors.join('\n')}`);
        }

      } catch (err: any) {
        console.error('Error during savings import:', err);
        setError(`Failed to import savings: ${err.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card rounded-md shadow-elevation-2 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Upload" size={20} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Import Savings</h3>
            <p className="text-sm text-muted-foreground">
              Bulk import savings from a CSV file (e.g. for missed days).
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-destructive font-medium whitespace-pre-wrap">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          <div className="p-4 bg-muted/30 rounded-md border border-border">
            <h4 className="text-sm font-semibold text-foreground mb-2">Step 1: Download Template</h4>
            <p className="text-xs text-muted-foreground mb-4">
              Use our standard format. Required columns: national_id, amount, date (YYYY-MM-DD).
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={downloadTemplate}
              className="w-full"
            >
              Download CSV Template
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-4 bg-muted/30 rounded-md border border-border">
              <h4 className="text-sm font-semibold text-foreground mb-2">Step 2: Upload Completed File</h4>
              <p className="text-xs text-muted-foreground mb-4">
                Select your completed CSV file.
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="w-full text-xs text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                disabled={loading || propLoading}
                ref={fileInputRef}
              />
              {file && (
                <p className="mt-2 text-xs text-success flex items-center gap-1">
                  <Icon name="CheckCircle" size={12} />
                  {file.name} selected
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading || propLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={loading || propLoading}
                disabled={!file}
                iconName="Upload"
                iconPosition="right"
              >
                Start Import
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ImportSavingsModal;
